import React, { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import { Mail, Webhook, Send, Plus, Trash2, Pencil, Play, Zap, MessageCircle, CalendarClock, CheckCircle2, XCircle, X } from "lucide-react";

const Card = ({ children, testid }) => (
  <div data-testid={testid} className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">{children}</div>
);
const Row = ({ label, desc, children }) => (
  <div className="flex items-start justify-between gap-4 py-3 border-b border-slate-100 last:border-0">
    <div className="min-w-0"><div className="text-sm font-medium text-slate-800">{label}</div>{desc && <div className="text-xs text-slate-500 mt-0.5">{desc}</div>}</div>
    <div className="shrink-0">{children}</div>
  </div>
);

export default function Integrations() {
  const [settings, setSettings] = useState(null);
  const [webhooks, setWebhooks] = useState([]);
  const [outbox, setOutbox] = useState([]);
  const [modal, setModal] = useState(null);
  const [deliv, setDeliv] = useState(null);
  const [testing, setTesting] = useState(false);
  const [conn, setConn] = useState(null);

  const loadSettings = useCallback(() => api.get("/integrations/settings").then((r) => setSettings(r.data)).catch(() => {}), []);
  const loadWebhooks = useCallback(() => api.get("/integrations/webhooks").then((r) => setWebhooks(r.data)).catch(() => {}), []);
  const loadOutbox = useCallback(() => api.get("/integrations/email/outbox", { params: { limit: 20 } }).then((r) => setOutbox(r.data)).catch(() => {}), []);
  const loadConn = useCallback(() => api.get("/connectors").then((r) => setConn(r.data)).catch(() => {}), []);
  useEffect(() => { loadSettings(); loadWebhooks(); loadOutbox(); loadConn(); }, [loadSettings, loadWebhooks, loadOutbox, loadConn]);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("calendar") === "connected") { toast.success("Google Calendar connected"); loadConn(); }
    else if (p.get("calendar") === "error") { toast.error("Google Calendar connection failed. Check your credentials and try again."); }
    if (p.has("calendar")) { p.delete("calendar"); window.history.replaceState({}, "", `${window.location.pathname}${p.toString() ? "?" + p.toString() : ""}`); }
  }, [loadConn]);

  const patch = async (p) => {
    try { const { data } = await api.put("/integrations/settings", p); setSettings((s) => ({ ...s, ...data })); toast.success("Saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const sendTest = async () => {
    setTesting(true);
    try { const { data } = await api.post("/integrations/email/test"); toast.success(`Test email sent to ${data.to}`); loadOutbox(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    finally { setTesting(false); }
  };
  const delWebhook = async (id) => {
    if (!window.confirm("Delete this webhook?")) return;
    await api.delete(`/integrations/webhooks/${id}`); toast.success("Webhook deleted"); loadWebhooks();
  };
  const testWebhook = async (id) => {
    try { const { data } = await api.post(`/integrations/webhooks/${id}/test`); toast[data.ok ? "success" : "error"](`Test delivery: ${data.status_code || "no response"}`); loadWebhooks(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Test failed"); }
  };

  if (!settings) return <div className="text-sm text-slate-400 py-8">Loading integrations…</div>;

  return (
    <div className="space-y-6" data-testid="integrations-panel">
      {/* EMAIL */}
      <Card testid="integrations-email">
        <div className="flex items-center gap-2 mb-3"><Mail size={18} className="text-blue-600" /><h3 className="font-semibold text-slate-900">Email Notifications</h3></div>
        <p className="text-xs text-slate-500 mb-2">Delivered via Emergent-managed Resend from “{settings.email_from_name}”. Recipients must be real addresses (test/demo domains are blocked by the provider).</p>
        <Row label="Enable email delivery" desc="Master switch for all outgoing notification emails">
          <Switch data-testid="email-enabled-switch" checked={!!settings.email_enabled} onCheckedChange={(v) => patch({ email_enabled: v })} />
        </Row>
        <Row label="Mirror all in-app notifications" desc="When on, every in-app notification is also emailed to the recipient">
          <Switch data-testid="email-mirror-switch" checked={!!settings.email_mirror_all} onCheckedChange={(v) => patch({ email_mirror_all: v })} />
        </Row>
        <div className="flex items-center gap-3 pt-3">
          <Button size="sm" onClick={sendTest} disabled={testing || !settings.email_enabled} data-testid="send-test-email">
            <Send size={14} className="mr-1.5" /> {testing ? "Sending…" : "Send test email"}
          </Button>
          <span className="text-xs text-slate-400">Sends to your own account email.</span>
        </div>
        {outbox.length > 0 && (
          <div className="mt-4 border-t border-slate-100 pt-3">
            <div className="text-xs font-semibold text-slate-500 mb-2">Recent email activity</div>
            <div className="space-y-1 max-h-48 overflow-auto">
              {outbox.map((r) => (
                <div key={r.id} className="flex items-center gap-2 text-xs">
                  {r.status === "sent" ? <CheckCircle2 size={13} className="text-emerald-500 shrink-0" /> : r.status === "failed" ? <XCircle size={13} className="text-red-500 shrink-0" /> : <span className="h-2 w-2 rounded-full bg-amber-400 shrink-0" />}
                  <span className="text-slate-600 truncate flex-1">{r.to}</span>
                  <span className="text-slate-400">{r.notif_type}</span>
                  <span className={`capitalize ${r.status === "sent" ? "text-emerald-600" : r.status === "failed" ? "text-red-600" : "text-amber-600"}`}>{r.status}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>

      {/* WEBHOOKS */}
      <Card testid="integrations-webhooks">
        <div className="flex items-center gap-2 mb-3">
          <Webhook size={18} className="text-indigo-600" /><h3 className="font-semibold text-slate-900">Outgoing Webhooks</h3>
          <Button size="sm" className="ml-auto" onClick={() => setModal({})} data-testid="add-webhook-button"><Plus size={14} className="mr-1" /> Add Webhook</Button>
        </div>
        <Row label="Enable webhook delivery" desc="Master switch — POST task events to your external endpoints (HMAC-signed)">
          <Switch data-testid="webhooks-enabled-switch" checked={!!settings.webhooks_enabled} onCheckedChange={(v) => patch({ webhooks_enabled: v })} />
        </Row>
        <div className="mt-3 space-y-2">
          {webhooks.length === 0 && <div className="text-sm text-slate-400 py-4 text-center">No webhooks yet. Add one to receive task events.</div>}
          {webhooks.map((w) => (
            <div key={w.id} data-testid={`webhook-row-${w.id}`} className="border border-slate-200 rounded-lg p-3">
              <div className="flex items-center gap-2">
                <span className={`h-2 w-2 rounded-full ${w.active ? "bg-emerald-500" : "bg-slate-300"}`} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-slate-800 truncate">{w.name}</div>
                  <div className="text-xs text-slate-400 font-mono truncate">{w.url}</div>
                </div>
                <button title="Send test" onClick={() => testWebhook(w.id)} data-testid={`webhook-test-${w.id}`} className="h-8 w-8 inline-flex items-center justify-center rounded-md text-slate-400 hover:text-emerald-600 hover:bg-emerald-50"><Play size={14} /></button>
                <button title="View deliveries" onClick={() => setDeliv(w)} data-testid={`webhook-deliveries-${w.id}`} className="h-8 px-2 text-xs rounded-md text-slate-500 hover:bg-slate-100">Logs</button>
                <button title="Edit" onClick={() => setModal(w)} data-testid={`webhook-edit-${w.id}`} className="h-8 w-8 inline-flex items-center justify-center rounded-md text-slate-400 hover:text-blue-600 hover:bg-blue-50"><Pencil size={14} /></button>
                <button title="Delete" onClick={() => delWebhook(w.id)} data-testid={`webhook-delete-${w.id}`} className="h-8 w-8 inline-flex items-center justify-center rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50"><Trash2 size={14} /></button>
              </div>
              <div className="flex flex-wrap gap-1 mt-2">
                {(w.events || []).map((e) => <span key={e} className="text-[10px] bg-slate-100 text-slate-600 rounded px-1.5 py-0.5 font-mono">{e}</span>)}
                {w.last_status != null && <span className={`text-[10px] rounded px-1.5 py-0.5 ${w.last_ok ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-600"}`}>last: {w.last_status}</span>}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* CONNECTORS */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <WhatsAppCard conn={conn} onReload={loadConn} />
        <GoogleCalendarCard conn={conn} onReload={loadConn} />
      </div>

      {modal && <WebhookModal wh={modal.id ? modal : null} events={settings.webhook_events || []} onClose={() => setModal(null)} onDone={() => { setModal(null); loadWebhooks(); }} />}
      {deliv && <DeliveriesModal wh={deliv} onClose={() => setDeliv(null)} />}
    </div>
  );
}

const StatusPill = ({ ok, okText, offText }) => (
  <span className={`ml-auto text-[10px] rounded px-1.5 py-0.5 ${ok ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{ok ? okText : offText}</span>
);

function WhatsAppCard({ conn, onReload }) {
  const wa = conn?.whatsapp;
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);
  useEffect(() => {
    if (wa) setF({ enabled: wa.enabled, graph_api_version: wa.graph_api_version || "v21.0", phone_number_id: wa.phone_number_id || "", waba_id: wa.waba_id || "", default_country_code: wa.default_country_code || "", access_token: "" });
  }, [wa]);
  if (!wa || !f) return <Card testid="integrations-whatsapp"><div className="text-sm text-slate-400">Loading…</div></Card>;
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    setSaving(true);
    try { await api.put("/connectors/whatsapp", f); toast.success("WhatsApp settings saved"); set("access_token", ""); onReload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  const sendTest = async () => {
    if (!testTo.trim()) return toast.error("Enter a recipient number (with country code)");
    setTesting(true);
    try { const { data } = await api.post("/connectors/whatsapp/test", { to: testTo.trim() }); toast.success(`Message accepted${data.message_id ? ` (${data.message_id.slice(0, 12)}…)` : ""}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    finally { setTesting(false); }
  };
  return (
    <Card testid="integrations-whatsapp">
      <div className="flex items-center gap-2 mb-1"><MessageCircle size={18} className="text-green-600" /><h3 className="font-semibold text-slate-900">WhatsApp</h3><StatusPill ok={wa.configured} okText="Configured" offText="Needs credentials" /></div>
      <p className="text-xs text-slate-500 mb-3">Meta WhatsApp Business Cloud API. When configured & enabled, tasks turning <strong>Critical</strong> or <strong>Blocked</strong> automatically WhatsApp the action + accountable owners (who have a mobile number and haven't opted out). Credentials are stored securely and never shown after saving.</p>
      <Row label="Enable WhatsApp" desc="Master switch for WhatsApp sending">
        <Switch data-testid="wa-enabled-switch" checked={!!f.enabled} onCheckedChange={(v) => set("enabled", v)} />
      </Row>
      <div className="grid grid-cols-2 gap-3 pt-3">
        <div><Label className="text-xs">Phone Number ID</Label><Input data-testid="wa-phone-id" value={f.phone_number_id} onChange={(e) => set("phone_number_id", e.target.value)} placeholder="e.g. 123456789012345" /></div>
        <div><Label className="text-xs">Graph API version</Label><Input data-testid="wa-api-version" value={f.graph_api_version} onChange={(e) => set("graph_api_version", e.target.value)} placeholder="v21.0" /></div>
        <div><Label className="text-xs">WABA ID (optional)</Label><Input data-testid="wa-waba-id" value={f.waba_id} onChange={(e) => set("waba_id", e.target.value)} placeholder="Business Account ID" /></div>
        <div><Label className="text-xs">Default country code</Label><Input data-testid="wa-country-code" value={f.default_country_code} onChange={(e) => set("default_country_code", e.target.value)} placeholder="e.g. 91" /></div>
        <div className="col-span-2"><Label className="text-xs">Access token {wa.access_token_set && <span className="text-slate-400">(saved — leave blank to keep)</span>}</Label><Input data-testid="wa-access-token" type="password" value={f.access_token} onChange={(e) => set("access_token", e.target.value)} placeholder={wa.access_token_set ? "••••••••" : "Permanent system-user token"} /></div>
      </div>
      <div className="flex items-center gap-2 pt-3">
        <Button size="sm" onClick={save} disabled={saving} data-testid="wa-save">{saving ? "Saving…" : "Save"}</Button>
      </div>
      <div className="mt-3 border-t border-slate-100 pt-3">
        <Label className="text-xs">Send a test message</Label>
        <div className="flex items-center gap-2 mt-1">
          <Input data-testid="wa-test-to" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="Recipient e.g. +14155552671" className="flex-1" />
          <Button size="sm" variant="outline" onClick={sendTest} disabled={testing || !wa.configured} data-testid="wa-test-send"><Send size={14} className="mr-1.5" />{testing ? "Sending…" : "Test"}</Button>
        </div>
        {!wa.configured && <p className="text-[11px] text-amber-600 mt-1">Enable and save valid credentials to send.</p>}
      </div>
    </Card>
  );
}

function GoogleCalendarCard({ conn, onReload }) {
  const gc = conn?.google_calendar;
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (gc) setF({ client_id: gc.client_id || "", client_secret: "", calendar_id: gc.calendar_id || "primary" });
  }, [gc]);
  if (!gc || !f) return <Card testid="integrations-calendar"><div className="text-sm text-slate-400">Loading…</div></Card>;
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    setSaving(true);
    try { await api.put("/connectors/google-calendar", f); toast.success("Calendar settings saved"); set("client_secret", ""); onReload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  const connect = async () => {
    setBusy(true);
    try { const { data } = await api.get("/connectors/google-calendar/auth-url"); window.location.href = data.url; }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not start connection"); setBusy(false); }
  };
  const disconnect = async () => {
    if (!window.confirm("Disconnect Google Calendar?")) return;
    try { await api.post("/connectors/google-calendar/disconnect"); toast.success("Disconnected"); onReload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const test = async () => {
    setBusy(true);
    try { const { data } = await api.post("/connectors/google-calendar/test"); toast.success("Test event created & cleaned up — connection works"); if (data.html_link) console.log("Test event:", data.html_link); }
    catch (e) { toast.error(e?.response?.data?.detail || "Test failed"); }
    finally { setBusy(false); }
  };
  return (
    <Card testid="integrations-calendar">
      <div className="flex items-center gap-2 mb-1"><CalendarClock size={18} className="text-blue-600" /><h3 className="font-semibold text-slate-900">Google Calendar</h3><StatusPill ok={gc.connected} okText="Connected" offText={gc.configured ? "Not connected" : "Needs credentials"} /></div>
      <p className="text-xs text-slate-500 mb-3">Connect via Google OAuth. Once connected & enabled, task due dates (all-day events) and meetings are automatically mirrored into this calendar, and update when dates change.</p>
      <div className="grid grid-cols-1 gap-3">
        <div><Label className="text-xs">Client ID</Label><Input data-testid="gc-client-id" value={f.client_id} onChange={(e) => set("client_id", e.target.value)} placeholder="xxxxx.apps.googleusercontent.com" /></div>
        <div><Label className="text-xs">Client Secret {gc.client_secret_set && <span className="text-slate-400">(saved — leave blank to keep)</span>}</Label><Input data-testid="gc-client-secret" type="password" value={f.client_secret} onChange={(e) => set("client_secret", e.target.value)} placeholder={gc.client_secret_set ? "••••••••" : "GOCSPX-…"} /></div>
        <div><Label className="text-xs">Calendar ID</Label><Input data-testid="gc-calendar-id" value={f.calendar_id} onChange={(e) => set("calendar_id", e.target.value)} placeholder="primary" /></div>
      </div>
      <div className="mt-2 rounded-md bg-slate-50 border border-slate-100 p-2">
        <div className="text-[11px] text-slate-500">Add this exact redirect URI in Google Cloud Console → Credentials:</div>
        <div className="text-[11px] font-mono text-slate-700 break-all mt-0.5" data-testid="gc-redirect-uri">{gc.redirect_uri}</div>
      </div>
      <div className="flex flex-wrap items-center gap-2 pt-3">
        <Button size="sm" onClick={save} disabled={saving} data-testid="gc-save">{saving ? "Saving…" : "Save"}</Button>
        {!gc.connected
          ? <Button size="sm" variant="outline" onClick={connect} disabled={busy || !gc.configured} data-testid="gc-connect">{busy ? "Redirecting…" : "Connect Google"}</Button>
          : <>
              <Button size="sm" variant="outline" onClick={test} disabled={busy} data-testid="gc-test"><Send size={14} className="mr-1.5" />{busy ? "Testing…" : "Test"}</Button>
              <Button size="sm" variant="outline" onClick={disconnect} data-testid="gc-disconnect" className="text-red-600 hover:text-red-700">Disconnect</Button>
            </>}
      </div>
      {gc.connected && gc.connected_email && <p className="text-[11px] text-emerald-600 mt-2" data-testid="gc-connected-email">Connected as {gc.connected_email}</p>}
      {!gc.configured && <p className="text-[11px] text-amber-600 mt-2">Enter Client ID & Secret, save, then Connect.</p>}
    </Card>
  );
}

function WebhookModal({ wh, events, onClose, onDone }) {
  const [f, setF] = useState({ name: wh?.name || "", url: wh?.url || "", events: wh?.events || [], secret: "", active: wh?.active ?? true });
  const toggleEvent = (e) => setF((p) => ({ ...p, events: p.events.includes(e) ? p.events.filter((x) => x !== e) : [...p.events, e] }));
  const submit = async () => {
    if (!f.name.trim() || !f.url.trim()) return toast.error("Name and URL required");
    try {
      if (wh) await api.put(`/integrations/webhooks/${wh.id}`, f);
      else await api.post("/integrations/webhooks", f);
      toast.success(wh ? "Webhook updated" : "Webhook created"); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="webhook-modal">
        <DialogHeader><DialogTitle>{wh ? "Edit Webhook" : "Add Webhook"}</DialogTitle><DialogDescription className="sr-only">Configure an outgoing webhook endpoint and the events it subscribes to.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label>Name *</Label><Input data-testid="webhook-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="e.g. Slack relay" /></div>
          <div><Label>Endpoint URL *</Label><Input data-testid="webhook-url" value={f.url} onChange={(e) => setF({ ...f, url: e.target.value })} placeholder="https://…" /></div>
          <div>
            <Label>Events</Label>
            <div className="grid grid-cols-2 gap-1.5 mt-1">
              {events.map((e) => (
                <label key={e} className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                  <Checkbox checked={f.events.includes(e)} onCheckedChange={() => toggleEvent(e)} data-testid={`webhook-event-${e}`} /> <span className="font-mono">{e}</span>
                </label>
              ))}
            </div>
          </div>
          <div><Label>Signing secret {wh && <span className="text-xs text-slate-400">(leave blank to keep existing)</span>}</Label><Input data-testid="webhook-secret" value={f.secret} onChange={(e) => setF({ ...f, secret: e.target.value })} placeholder="Used for X-TaskMatrix-Signature (HMAC-SHA256)" /></div>
          <label className="flex items-center gap-2 text-sm"><Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} data-testid="webhook-active" /> Active</label>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={submit} data-testid="webhook-submit">{wh ? "Save" : "Create"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeliveriesModal({ wh, onClose }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get(`/integrations/webhooks/${wh.id}/deliveries`).then((r) => setRows(r.data)).catch(() => setRows([])); }, [wh.id]);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="webhook-deliveries-modal">
        <DialogHeader><DialogTitle>Deliveries — {wh.name}</DialogTitle><DialogDescription className="sr-only">Recent webhook delivery attempts and their responses.</DialogDescription></DialogHeader>
        <div className="max-h-96 overflow-auto space-y-2">
          {rows === null && <div className="text-sm text-slate-400">Loading…</div>}
          {rows && rows.length === 0 && <div className="text-sm text-slate-400">No deliveries yet.</div>}
          {rows && rows.map((d) => (
            <div key={d.id} className="border border-slate-200 rounded-lg p-2 text-xs">
              <div className="flex items-center gap-2">
                {d.ok ? <CheckCircle2 size={13} className="text-emerald-500" /> : <XCircle size={13} className="text-red-500" />}
                <span className="font-mono font-medium">{d.event}</span>
                <span className={d.ok ? "text-emerald-600" : "text-red-600"}>{d.status_code || "—"}</span>
                <span className="ml-auto text-slate-400">{new Date(d.created_at).toLocaleString()}</span>
              </div>
              {d.response && <div className="mt-1 text-slate-500 font-mono truncate">{d.response}</div>}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
